import React, { useState, useContext, useEffect, useLayoutEffect, useRef } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { TrialContainer } from "./styles";
import { PrimaryButton } from "../studioxr/Button";
import { setProfileFirstAction } from "../libs/util.ts";
import { identifyUser, trackEvent, trackEventMeta } from "../libs/analyticsManager.ts";
import { useLoginState } from "../studioxr/auth/LoginState.ts";
import { AuthContext } from "../auth/Auth";
import { ProfileFirstActions, ProfileInterface, UserProfileRole } from "ts-interfaces";
import { firebaseHelper, cloudflareCDN } from "ui";
import { useMattoState } from "../MattoState.ts";
import { getGoogleAnalyticsProductInfo, getMetaPurchaseParams } from "./config.js";
import { clearCheckoutSource, peekCheckoutSource } from "./checkoutSource";

import { LookupKey, useStripeStore, ProfilePrices } from "./useStripeStore.tsx";
import { MONTHLY_PRO_CREDITS, ANNUAL_PRO_CREDITS } from "../constants/proCredits";
import { ChoosePlan } from "./ChoosePlan.tsx";
import { PlanOverview, PlanPair } from "./PlanOverview.tsx";
import { FinalizePayment } from "./FinalizePayment.tsx";
import { useCreditStore, suppressBalanceListener, OPTIMISTIC_SUPPRESS_MS } from "ui";

export const StartSubscription = ({ hideClose = false, initialStep = 0, leaveForLibrary = false, trackView = true, onStepChange }: { hideClose?: boolean; initialStep?: number; leaveForLibrary?: boolean; trackView?: boolean; onStepChange?: (step: number | null) => void } = {}) => {
  const profile:ProfileInterface | any = useLoginState((state) => state.profile);
  const [activeStep, setActiveStep] = React.useState(initialStep);
  const setShowSubscription = useMattoState((state) => state.setShowSubscription);
  const navigate = useNavigate();
  const profile_prices = useStripeStore(state => state.profile_prices)
  const setProfile = useLoginState((state) => state.setProfile);
  const { currentUser } = useContext(AuthContext);

  const [selectedPlan, setSelectedPlan] = useState<ProfilePrices | any>(null);
  const fetchBalance = useCreditStore((state) => state.fetchBalance);
  const isStudent = profile?.occupation=='Student'

  const ready = Boolean(profile) && Object.keys(profile_prices).length > 0;

  useEffect(() => {  useStripeStore.getState().setProductAndPrices()  } ,[])
  // Lets the host modal widen its shell for the plan overview (step 0); a layout effect so the
  // shell is sized before the first paint. null while nothing renders, so a phone user without a
  // profile keeps the default box (backdrop to tap) instead of a blank full-screen sheet.
  useLayoutEffect(() => { onStepChange?.(ready ? activeStep : null); }, [activeStep, ready]);

  const plan: PlanPair = {
    monthly: isStudent ? profile_prices[LookupKey.student_monthly] : profile_prices[LookupKey.pro_monthly],
    yearly: isStudent ? profile_prices[LookupKey.student_yearly] : profile_prices[LookupKey.pro_yearly],
  }
  // Team seats have their own Prices (never a student one), as in the Create Team wizard.
  const teamPrices = { monthly: profile_prices[LookupKey.team_monthly], yearly: profile_prices[LookupKey.team_yearly] }

  const handleModalClose = () => void  setShowSubscription(null);

  const handleSuccessfulPayment = (stripeCustomerId, subscriptionId) => {
    if(stripeCustomerId && subscriptionId){
      const dataToUpdate = {
        stripeCustomerId: stripeCustomerId,
        stripeSubscriptionId: subscriptionId,
        userRole: UserProfileRole.PRO
        // trialends: getDateDaysFromNow(30,true)  
      }
      // Soft-add subscription credits so the UI shows them immediately.
      // Suppress the Firestore listener briefly so it doesn't overwrite with stale data.
      // The listener will pick up the real balance once the webhook updates creditBalances.
      const optimisticCredits = selectedPlan?.planType === 'yearly' ? ANNUAL_PRO_CREDITS : MONTHLY_PRO_CREDITS;
      suppressBalanceListener();
      useCreditStore.getState().addPendingArtifactCredits(-optimisticCredits);
      firebaseHelper.updateFieldsInRecord(profile.id, 'profiles', dataToUpdate).then(res => {
          const updatedProfile = {...profile, ...dataToUpdate}
          setProfile(updatedProfile);
          identifyUser({ currentUser, profile: updatedProfile });
          // Delayed authoritative fetch after suppression window expires
          setTimeout(() => fetchBalance(true), OPTIMISTIC_SUPPRESS_MS);
          setActiveStep(3);
      }).catch((err) => {
        console.error(err);
        setShowSubscription(null);
      })
    }
  }
  
  if (!ready) return null;
  return(
    <TrialContainer>
    {activeStep !== 0 && (
    <img
      src={cloudflareCDN(window.location.origin + "/images/upgrade-pro-graphic-updated-20261013.jpg", "width=1000,fit=scale-down,format=auto")}
      alt=""
      className="trial-graphic"
      aria-hidden="true"
    />
    )}
    {!hideClose && (
      <IconButton sx={{zIndex:'100'}} onClick={handleModalClose} className="close-btn" aria-label="Close">
        <CloseIcon sx={{width: "24px", height: "24px"}} />
      </IconButton>
    )}
    <div className="form-root trial" style={activeStep === 0 ? { paddingRight: 0 } : undefined}>
          {activeStep === 0 && (
              <PlanOverview
                proPrices={plan}
                teamPrices={teamPrices}
                trackView={trackView}
                onUpgrade={() => { setActiveStep(1); }}/>
          )}
          {activeStep === 1 && (
              <ChoosePlan profilePrices={ plan } 
                setSelectedPlan={setSelectedPlan} 
                onSuccess={() => setActiveStep(2)} 
                onBack={() => setActiveStep(0)} />
          )}
          {activeStep === 2 && selectedPlan && (
              <FinalizePayment
                selectedPlan={selectedPlan}
                onSuccess={handleSuccessfulPayment}
                onBack={() =>  setActiveStep(1)} />
          )}
          {activeStep === 3 && selectedPlan && (
              <PaymentSuccessView
                selectedPlan={selectedPlan}
                // handleSuccessfulPayment writes this to the profile before step 3.
                subscriptionId={profile?.stripeSubscriptionId}
                onSuccess={() => {
                  setShowSubscription(null);
                  // Opened via the plan picker (the /for-sketchup landing):
                  // "Start Designing" leaves the landing page for the library.
                  if (leaveForLibrary) navigate("/home/library");
                }} />
          )}
    </div>
    </TrialContainer>
  )
}

  const PaymentSuccessView = ({ onSuccess, selectedPlan, subscriptionId }) => {
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    setProfileFirstAction(ProfileFirstActions.PAYMENT_SUCCESS_MODAL).finally(()=>{});
    const source = peekCheckoutSource();
    trackEvent("Subscription - Purchase Success", {
      plan: selectedPlan?.nickname || selectedPlan?.lookup_key || '',
      ...(source ? { source } : {}),
    });
    clearCheckoutSource();
    if (window.gtag && selectedPlan) window.gtag("event", "purchase", getGoogleAnalyticsProductInfo(selectedPlan));
    // A Purchase without value/currency is worse than none for the ad account.
    const metaPurchase = selectedPlan ? getMetaPurchaseParams(selectedPlan) : null;
    if (metaPurchase && subscriptionId) trackEventMeta("Purchase", metaPurchase, `purchase_${subscriptionId}`);
  }, [selectedPlan]);

  const complete = () => { onSuccess && onSuccess();  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'safe center',
      padding: "16px 25px",
      overflowY: 'auto',
      width: '100%',
      maxWidth: '420px',
      marginLeft: 'auto',
      marginRight: 'auto',
      boxSizing: 'border-box',
    }}>
      <Typography sx={{ fontSize: '24px', fontWeight: 400, textAlign: 'center', marginBottom: '24px' }}>
        Mattoboard Pro is Successfully Activated!
      </Typography>
      <PrimaryButton
        fullWidth
        variant="contained"
        onClick={complete}
        sx={{
          padding: '8px',
          fontWeight: 400,
          fontSize: '18px',
          textTransform: 'capitalize',
          borderRadius: '8px',
          background: '#000',
          color: '#fff',
          '&:hover': { background: '#222' },
        }}
      >
        Start Designing
      </PrimaryButton>
    </Box>
  );
};

